import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  TextField,
  IconButton,
  MenuItem,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Call, Close, CallEnd } from "@mui/icons-material";
import { useCallContext } from "../../context/CallContext";
import ChannelSelector from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import { CHANNEL_TYPES } from "../../constants/channelCatalog";
import {
  normalizeDialPhone,
  formatPhoneAsYouType,
  formatPhoneDisplay,
} from "../../utils/phone";

const MessagePhoneChannel = ({
  selectedChannel,
  setSelectedChannel,
  conversationId,
  userPhone = "",
  customerPhones = [],
  origin,
  setOrigin,
  destination,
  setDestination,
  clientConversation = null,
  conversationMessages = null,
}) => {
  const theme = useTheme();
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const channelTipo = Number(currentChannel?.tipo);
  const isPabx = channelTipo === CHANNEL_TYPES.PABX;
  const isTwilioPhone = channelTipo === CHANNEL_TYPES.TWILIO_PHONE;
  const pabxOriginateUrl = String(currentChannel?.pabx_originate_url || "").trim();
  const pabxCanOriginate = isPabx && Boolean(pabxOriginateUrl);
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType: "Phone",
    channelName,
    isInternalNote: false,
  });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [customOrigin, setCustomOrigin] = useState("");
  const [customDestination, setCustomDestination] = useState("");
  const [callError, setCallError] = useState(null);
  const [calling, setCalling] = useState(false);
  const fieldSize = "small";

  const destinationOptions = useMemo(() => {
    const list = Array.isArray(customerPhones) ? customerPhones.filter(Boolean) : [];
    const seen = new Set();
    const out = [];
    for (const raw of list) {
      const n = normalizeDialPhone(raw);
      const value = n.ok ? n.e164 : String(raw).trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      out.push({
        value,
        label: (n.ok && n.display) || formatPhoneDisplay(value) || value,
      });
    }
    return out;
  }, [customerPhones]);

  useEffect(() => {
    if (destination === "custom") return;
    const values = destinationOptions.map((o) => o.value);
    if (destination && values.includes(destination)) return;
    if (destination) {
      const n = normalizeDialPhone(destination);
      const match = values.find((v) => n.ok && v === n.e164);
      if (match) {
        setDestination(match);
        return;
      }
    }
    if (values[0]) setDestination(values[0]);
  }, [destination, destinationOptions, setDestination]);

  const fieldSx = {
    "& .MuiInputBase-root": {
      height: 44,
      borderRadius: 12,
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
    },
  };

  const { outgoingCall, startOutgoingCall, endOutgoingCall } = useCallContext();

  const handleCall = async () => {
    setCallError(null);
    const finalOrigin = origin === "custom" ? customOrigin : origin;
    const finalDestination = destination === "custom" ? customDestination : destination;

    if (!finalDestination || finalDestination === "custom") return;
    if (!selectedChannel) return;

    if (isPabx && !pabxCanOriginate) {
      setCallError(
        "Configure a URL da API de originação neste canal de Telefonia (Canais) para poder discar."
      );
      return;
    }

    // Softphone Twilio só no canal Telefonia Xpro (tipo 5).
    if (!isPabx && !isTwilioPhone) {
      setCallError("Este canal de telefonia não permite originar chamadas.");
      return;
    }

    const toNorm = normalizeDialPhone(finalDestination);
    if (!toNorm.ok) {
      setCallError(toNorm.error || "Número de destino inválido.");
      return;
    }

    let fromNormalized;
    if (finalOrigin && finalOrigin !== "computer" && finalOrigin !== "custom") {
      const fromNorm = normalizeDialPhone(finalOrigin);
      if (!fromNorm.ok) {
        setCallError(fromNorm.error || "Número de origem inválido.");
        return;
      }
      fromNormalized = fromNorm.e164;
    } else if (!isPabx) {
      // Softphone (Client SDK): o Dial precisa de callerId — usa o número do canal.
      const channelPhone =
        currentChannel?.twilio_phone_number ||
        currentChannel?.telefone ||
        currentChannel?.phone ||
        "";
      const channelFrom = normalizeDialPhone(channelPhone);
      if (channelFrom.ok) {
        fromNormalized = channelFrom.e164;
      } else {
        setCallError(
          "Canal sem número de origem. Configure o telefone do canal de Telefonia Xpro."
        );
        return;
      }
    }

    setCalling(true);
    try {
      const usePstnOrigin =
        isTwilioPhone &&
        Boolean(fromNormalized) &&
        finalOrigin &&
        finalOrigin !== "computer";
      await startOutgoingCall({
        mode: isPabx ? "pabx" : usePstnOrigin ? "twilio_pstn" : "twilio",
        channelId: Number(selectedChannel) || null,
        leadId: Number(conversationId) || null,
        fromNumber: fromNormalized,
        toNumber: toNorm.e164,
        ramal: isPabx ? currentChannel?.pabx_default_ramal || undefined : undefined,
      });
    } catch (err) {
      setCallError(err?.message || "Não foi possível originar a chamada.");
    } finally {
      setCalling(false);
    }
  };

  const dialDisabled =
    calling ||
    (isPabx && !pabxCanOriginate) ||
    (!isPabx && !isTwilioPhone) ||
    (origin === "custom" && !customOrigin) ||
    (destination === "custom" && !customDestination) ||
    !origin ||
    !destination;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        overflow: "hidden",
        minWidth: 0,
        ...composerSurface.shell,
      }}
    >
      {isPabx && !pabxCanOriginate ? (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Canal Telefonia (central do cliente) sem URL de originação. Configure em Canais para
          liberar a discagem.
        </Alert>
      ) : null}
      {callError ? (
        <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setCallError(null)}>
          {callError}
        </Alert>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isMobile ? "flex-start" : "center",
            flexShrink: 0,
            px: 0.25,
            ...composerSurface.divider,
          }}
        >
          <ChannelSelector
            integrated
            selectedChannel={selectedChannel}
            setSelectedChannel={setSelectedChannel}
            clientConversation={clientConversation}
            conversationMessages={conversationMessages}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: 0.75,
            px: 1,
            py: 0.75,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column" }}>
            {origin !== "custom" ? (
              <TextField
                select
                label="Origem"
                size={fieldSize}
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                fullWidth
                sx={fieldSx}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "top", horizontal: "left" },
                    transformOrigin: { vertical: "bottom", horizontal: "left" },
                  },
                }}
              >
                {!isPabx ? <MenuItem value="computer">Computador</MenuItem> : null}
                {userPhone ? <MenuItem value={userPhone}>Meu Telefone ({userPhone})</MenuItem> : null}
                <MenuItem value="custom">Outro</MenuItem>
              </TextField>
            ) : (
              <TextField
                label="Digite o número de origem"
                size={fieldSize}
                value={customOrigin}
                onChange={(e) => setCustomOrigin(formatPhoneAsYouType(e.target.value))}
                placeholder="Ex.: (11) 91234-5678"
                fullWidth
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOrigin(isPabx && userPhone ? userPhone : "computer")}>
                        <Close />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column" }}>
            {destination !== "custom" ? (
              <TextField
                select
                label="Destino"
                size={fieldSize}
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                fullWidth
                sx={fieldSx}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "top", horizontal: "left" },
                    transformOrigin: { vertical: "bottom", horizontal: "left" },
                  },
                }}
              >
                {destinationOptions.map((phone) => (
                  <MenuItem key={phone.value} value={phone.value}>
                    {phone.label}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Outro</MenuItem>
              </TextField>
            ) : (
              <TextField
                label="Digite o número de destino"
                size={fieldSize}
                value={customDestination}
                onChange={(e) => setCustomDestination(formatPhoneAsYouType(e.target.value))}
                placeholder="Ex.: (11) 91234-5678 ou +55…"
                fullWidth
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setDestination("")}>
                        <Close />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          <Tooltip
            title={
              isPabx && !pabxCanOriginate
                ? "Configure a URL de originação no canal"
                : isPabx
                  ? "Originar na central telefônica"
                  : "Ligar (Telefonia Xpro)"
            }
          >
            <span>
              <IconButton
                onClick={outgoingCall ? endOutgoingCall : handleCall}
                disabled={outgoingCall ? false : dialDisabled}
                sx={{
                  backgroundColor: outgoingCall ? theme.palette.error.main : theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "50%",
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: outgoingCall ? theme.palette.error.dark : theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                }}
              >
                {outgoingCall ? <CallEnd fontSize="large" /> : <Call fontSize="large" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {isPabx && pabxCanOriginate ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 0.75 }}>
          Discagem via API da central (marcadores #DESTINO#, #ORIGEM#, #RAMAL#…).
        </Typography>
      ) : null}
    </Box>
  );
};

export default MessagePhoneChannel;
